import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * One-time password setup using the legacy referrer magic-link `access_token`.
 *
 * Two entry points:
 *   /referrer-setup           — user pastes their token + chooses a password
 *   /referrer-setup/:token    — token comes from URL (link in welcome email)
 *
 * On success we store the JWT pair (same shape as /auth/login) and redirect
 * to the referrer dashboard.
 */
export default function ReferrerPasswordSetupPage() {
  const { token: urlToken } = useParams();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [token, setToken] = useState(urlToken || queryToken || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (urlToken) setToken(urlToken);
  }, [urlToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token.trim()) {
      setError("Paste the setup token from your welcome email");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post("/auth/setup-password", {
        access_token: token.trim(),
        password,
      });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      await checkAuth();
      setDone(true);
      // Brief success flash, then bounce to dashboard
      setTimeout(() => {
        const refToken = data.user?.referrer_token;
        navigate(refToken ? `/referrer-dashboard/${refToken}` : "/referrer-login");
      }, 1200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Setup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="referrer-setup-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8 p-4 rounded-lg" style={{ background: "#100f5d" }}>
          <img src="/logo.png" alt="UKC Legal Solicitors" className="h-10 w-auto" />
        </div>

        {done ? (
          <div className="text-center py-12" data-testid="referrer-setup-done">
            <CheckCircle weight="fill" className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Password set
            </h2>
            <p className="text-sm text-slate-500 mt-1">Redirecting you to your dashboard…</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2"
              style={{ fontFamily: "Outfit, sans-serif", color: "#100f5d" }}>
              Set up your password
            </h1>
            <p className="text-slate-500 mb-8 text-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
              Choose a password so you can sign in directly without the email link in future.
            </p>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex gap-2"
                data-testid="referrer-setup-error">
                <Warning weight="fill" className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!urlToken && (
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-sm font-medium text-slate-700">Setup token</Label>
                  <Input
                    id="token"
                    data-testid="referrer-setup-token-input"
                    placeholder="Paste from welcome email"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    className="h-11 font-mono text-xs"
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="pwd" className="text-sm font-medium text-slate-700">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="pwd"
                    type="password"
                    data-testid="referrer-setup-pwd-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11"
                    placeholder="At least 8 characters"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-sm font-medium text-slate-700">Confirm password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirm"
                    type="password"
                    data-testid="referrer-setup-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              <Button type="submit" disabled={submitting}
                data-testid="referrer-setup-submit-btn"
                className="w-full h-11 text-white font-medium rounded-lg"
                style={{ background: "#100f5d" }}>
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : "Set password & sign in"}
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-slate-500">
              Already set up? <Link to="/referrer-login" className="font-semibold hover:underline" style={{ color: "#100f5d" }}>Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
